import { UtilsService } from "@/client"
import { optimizeImageForUpload } from "@/utils/richTextImages"
import { Box, Flex, IconButton } from "@chakra-ui/react"
import Image from "@tiptap/extension-image"
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table"
import { type Editor, EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { useCallback, useEffect, useRef } from "react"
import {
  FaBold,
  FaColumns,
  FaItalic,
  FaListOl,
  FaListUl,
  FaMinus,
  FaPlus,
  FaStrikethrough,
  FaTable,
  FaTrash,
} from "react-icons/fa"
import "./rich-text-editor.css"

interface RichTextEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  minHeight?: string
  onUploadStateChange?: (isUploading: boolean) => void
  onUploadError?: (message: string) => void
}

export function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Start typing...",
  minHeight = "150px",
  onUploadStateChange,
  onUploadError,
}: RichTextEditorProps) {
  const editorRef = useRef<Editor | null>(null)
  const pendingUploadsRef = useRef(0)

  const setUploadPending = useCallback(
    (isPending: boolean) => {
      pendingUploadsRef.current += isPending ? 1 : -1
      if (pendingUploadsRef.current < 0) {
        pendingUploadsRef.current = 0
      }
      onUploadStateChange?.(pendingUploadsRef.current > 0)
    },
    [onUploadStateChange],
  )

  const uploadImageFiles = useCallback(
    async (files: File[]) => {
      const imageFiles = files.filter((file) => file.type.startsWith("image/"))
      if (imageFiles.length === 0) {
        return []
      }

      setUploadPending(true)
      try {
        const uploadedUrls: string[] = []
        for (const file of imageFiles) {
          const optimizedFile = await optimizeImageForUpload(file)
          const response = await UtilsService.uploadImageEndpoint({
            formData: { file: optimizedFile },
          })
          uploadedUrls.push(response.url)
        }
        return uploadedUrls
      } catch (error) {
        onUploadError?.(
          error instanceof Error ? error.message : "Failed to upload image",
        )
        return null
      } finally {
        setUploadPending(false)
      }
    },
    [onUploadError, setUploadPending],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // Disable code blocks to avoid eval issues
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    immediatelyRender: false, // Prevent SSR issues
    editorProps: {
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? [])
        const hasImages = files.some((file) => file.type.startsWith("image/"))
        if (!hasImages) {
          return false
        }

        event.preventDefault()
        const html =
          typeof event.clipboardData?.getData === "function"
            ? event.clipboardData.getData("text/html")
            : ""
        void (async () => {
          const uploadedUrls = await uploadImageFiles(files)
          if (!uploadedUrls) {
            return
          }

          if (!html) {
            for (const src of uploadedUrls) {
              editorRef.current?.chain().focus().setImage({ src }).run()
            }
            return
          }

          const container = document.createElement("div")
          container.innerHTML = html
          const images = Array.from(container.querySelectorAll("img"))
          let uploadedIndex = 0

          for (const image of images) {
            const src = image.getAttribute("src") ?? ""
            if (!src || src.startsWith("blob:") || src.startsWith("data:")) {
              const uploadedUrl = uploadedUrls[uploadedIndex]
              if (uploadedUrl) {
                image.setAttribute("src", uploadedUrl)
                uploadedIndex += 1
              }
            }
          }

          const remainingUrls = uploadedUrls.slice(uploadedIndex)
          for (const src of remainingUrls) {
            const image = document.createElement("img")
            image.src = src
            container.appendChild(image)
          }

          editorRef.current
            ?.chain()
            .focus()
            .insertContent(container.innerHTML)
            .run()
        })()
        return true
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? [])
        const hasImages = files.some((file) => file.type.startsWith("image/"))
        if (!hasImages) {
          return false
        }

        event.preventDefault()
        void (async () => {
          const uploadedUrls = await uploadImageFiles(files)
          if (uploadedUrls) {
            for (const src of uploadedUrls) {
              editorRef.current?.chain().focus().setImage({ src }).run()
            }
          }
        })()
        return true
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML())
    },
  })

  editorRef.current = editor

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  return (
    <Box
      width="100%"
      borderWidth="1px"
      borderRadius="md"
      minH={minHeight}
      position="relative"
      _focusWithin={{
        borderColor: "blue.500",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
      }}
    >
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} placeholder={placeholder} />
    </Box>
  )
}

interface EditorToolbarProps {
  editor: Editor | null
}

function EditorToolbar({ editor }: EditorToolbarProps) {
  if (!editor) {
    return null
  }

  return (
    <Flex
      borderBottomWidth="1px"
      p={2}
      gap={1}
      flexWrap="wrap"
      bg="gray.50"
      _dark={{ bg: "gray.800" }}
    >
      <IconButton
        size="sm"
        variant={editor.isActive("bold") ? "solid" : "outline"}
        colorPalette={editor.isActive("bold") ? "blue" : "gray"}
        onClick={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
      >
        <FaBold />
      </IconButton>

      <IconButton
        size="sm"
        variant={editor.isActive("italic") ? "solid" : "outline"}
        colorPalette={editor.isActive("italic") ? "blue" : "gray"}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
      >
        <FaItalic />
      </IconButton>

      <IconButton
        size="sm"
        variant={editor.isActive("strike") ? "solid" : "outline"}
        colorPalette={editor.isActive("strike") ? "blue" : "gray"}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        aria-label="Strikethrough"
      >
        <FaStrikethrough />
      </IconButton>

      <Box borderLeftWidth="1px" mx={1} />

      <IconButton
        size="sm"
        variant="outline"
        colorPalette="gray"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 2, cols: 2, withHeaderRow: true })
            .run()
        }
        aria-label="Insert Table"
      >
        <FaTable />
      </IconButton>

      {editor.isActive("table") && (
        <>
          <IconButton
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            aria-label="Add Table Row"
          >
            <FaPlus />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={() => editor.chain().focus().deleteRow().run()}
            aria-label="Delete Table Row"
          >
            <FaMinus />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            aria-label="Add Table Column"
          >
            <FaColumns />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline"
            colorPalette="gray"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            aria-label="Delete Table Column"
          >
            <FaMinus />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline"
            colorPalette="red"
            onClick={() => editor.chain().focus().deleteTable().run()}
            aria-label="Delete Table"
          >
            <FaTrash />
          </IconButton>
        </>
      )}

      <Box borderLeftWidth="1px" mx={1} />

      <IconButton
        size="sm"
        variant={editor.isActive("bulletList") ? "solid" : "outline"}
        colorPalette={editor.isActive("bulletList") ? "blue" : "gray"}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet List"
      >
        <FaListUl />
      </IconButton>

      <IconButton
        size="sm"
        variant={editor.isActive("orderedList") ? "solid" : "outline"}
        colorPalette={editor.isActive("orderedList") ? "blue" : "gray"}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Numbered List"
      >
        <FaListOl />
      </IconButton>

      <Box borderLeftWidth="1px" mx={1} />

      <IconButton
        size="sm"
        variant={editor.isActive("heading", { level: 2 }) ? "solid" : "outline"}
        colorPalette={
          editor.isActive("heading", { level: 2 }) ? "blue" : "gray"
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        aria-label="Heading 2"
      >
        <strong>H2</strong>
      </IconButton>

      <IconButton
        size="sm"
        variant={editor.isActive("heading", { level: 3 }) ? "solid" : "outline"}
        colorPalette={
          editor.isActive("heading", { level: 3 }) ? "blue" : "gray"
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        aria-label="Heading 3"
      >
        <strong>H3</strong>
      </IconButton>
    </Flex>
  )
}
